import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api.js';
import { invalidateTeamRoleCache } from '../hooks/useTeamRole.js';
import { resetOnboardingCheck } from '../services/onboardingCache.js';
import { cancelPendingSave } from '../utils/lastTeam.js';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    // 2026-07-20 (production bug: RSVP "I'm In" magic-login lands the user
    // logged in but with a GUTTED sidebar — zero teams, "Game not found")
    // — ROOT CAUSE #3. /rsvp/result?...&magic=... is reached via a full
    // HTTP redirect from the backend (not a client-side route change), so
    // this bootstrap probe and RsvpResultPage's POST /auth/magic-login
    // exchange both kick off on the SAME fresh page load, racing each
    // other. This probe reads whatever token happens to already be in
    // localStorage (commonly a STALE one — the reminder email is often
    // opened days after the user's last session, well past their access
    // token's lifetime). Its 401 → refresh → retry cycle (services/api.js)
    // can resolve and WRITE to localStorage at any point relative to the
    // magic exchange's own writes — there is no ordering guarantee between
    // two independent fetches. A bad ordering can land the freshly-mounted
    // layout's very first GET /teams asking with a token pair that's
    // mid-swap, or simply let the STALE call's plain setUser(data) (a full
    // replace, not a merge) clobber state right as the magic flow is
    // populating it. The v2.5.223 fix above stopped the WIPE half of this
    // race; it never addressed the "two independent writers, no ordering"
    // half. The robust fix is to not have two writers at all: when this
    // exact page load is the magic-login landing, RsvpResultPage owns
    // establishing the session — skip this probe entirely instead of
    // racing it. (The plain error-variant redirects on /rsvp/result, e.g.
    // 'expired'/'not_found' with NO magic token, still need the normal
    // bootstrap so an already-logged-in user who tapped a dead link keeps
    // their real session — only skip when `magic` is actually present.)
    if (window.location.pathname === '/rsvp/result') {
      const search = new URLSearchParams(window.location.search);
      if (search.get('magic')) {
        setLoading(false);
        return;
      }
    }
    const token = localStorage.getItem('token');
    if (!token) { setLoading(false); return; }
    try {
      // v2.5.223 — skipAuthRedirect: this is the boot-time session probe.
      // A 401 here just means "no valid session yet" (loadUser handles it
      // below); it must NOT trigger the api.js interceptor's hard
      // wipe + window.location redirect, which would clobber a concurrent
      // magic-login (the email "I'm In" auto-login on /rsvp/result).
      let data;
      try {
        ({ data } = await api.get('/auth/me', { skipAuthRedirect: true }));
      } catch (first) {
        // 2026-11-02 — one patient retry when the first probe timed out or
        // the network dropped (the server busy with a video-AI run): the
        // session is still there, the answer was just slow.
        const st = first?.response?.status;
        if ((st === 401 && !first.refreshTransient) || st === 403) throw first;
        ({ data } = await api.get('/auth/me', { skipAuthRedirect: true, timeout: 45_000 }));
      }
      setUser(data);
    } catch (err) {
      // 2026-11-02 — a TIMEOUT or network drop here (the server busy with a
      // video-AI run, a reload on a bad connection) is not a verdict on the
      // session; wiping on it was one more way to "keep getting logged
      // out". Only a 401/403 answer means the session is really gone.
      const st = err?.response?.status;
      if ((st !== 401 && st !== 403) || err?.refreshTransient) { setLoading(false); return; }
      // v2.5.223 — only wipe if the token hasn't changed underneath us.
      // A concurrent magic-login (RsvpResultPage exchanging an email
      // "I'm In" link) can store a FRESH token while this bootstrap
      // /auth/me — running with a STALE token from a previous session —
      // is still in flight. If that stale call 401s and we blindly remove
      // the tokens, we wipe the just-created magic-login session and bounce
      // the user to /login right after they were logged in (the reported
      // "I clicked I'm In but it didn't log me in"). Comparing guards that
      // race: if the stored token changed, a newer login owns it — leave it.
      if (localStorage.getItem('token') === token) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    localStorage.setItem('token', data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);
    // Apr 2026 — clear any team-role cache from a prior session on
    // this browser. Without this, when account A (a coach on team T)
    // logs out and account B (a spare on team T) logs in within the
    // 60-second cache TTL, useTeamRole(T) hands back A's cached
    // 'coach' role to B. Result: spare sees coach-only buttons
    // (Check In, ⬆ Top, kebab menu, etc.) on the waitlist for up to
    // 60s. The backend correctly rejects any presses with a 403,
    // but the UI shouldn't render the buttons in the first place.
    invalidateTeamRoleCache();
    // v2.5.146 — clear onboarding-check cache: a fresh login should
    // re-check (the new user may have different teams/leagues/invites
    // than whoever was last logged in on this device).
    resetOnboardingCheck();
    // Accept pending invite if any — BEFORE setUser + returning so the
    // dashboard that the caller navigates to sees the user on the team.
    // Previously this was fire-and-forget (.catch(() => {})) which meant
    // the invite-accept HTTP call was still in flight when /dashboard
    // loaded; the dashboard's "fetch my teams" request returned an empty
    // list and the user saw the "Create Team" empty state instead of
    // the team they were invited to. Awaiting fixes the ordering.
    //
    // We also surface accept failures via a thrown error so the caller
    // (LoginPage) can show a toast — silent failure was hiding "invite
    // expired" / "already accepted" from users entirely.
    const inviteToken = localStorage.getItem('pendingInviteToken');
    if (inviteToken) {
      try {
        await api.get(`/invite/${inviteToken}`);
      } catch (err) {
        // Don't unwind the login — they ARE logged in. But clear the
        // token (so we don't retry on every page load) and re-throw
        // with a helpful message. LoginPage can catch and display.
        localStorage.removeItem('pendingInviteToken');
        setUser(data.user);
        const reason = err.response?.data?.error;
        // Map the backend's reason string to a user-facing explanation.
        // handleInviteToken returns: 'expired' (not in DB / past expiry /
        // already-consumed single-use), 'wrong_account' (coach/manager
        // invite — role-escalation risk so strict email check applies),
        // or 'error' (DB failure). Player/spare invites DO NOT return
        // wrong_account anymore (Apr 2026) — they accept any email and
        // attach the role/seasons to whoever signed in.
        const userFacing =
          reason === 'expired'       ? 'Your invite has expired — ask your coach for a new link.'
        : reason === 'wrong_account' ? 'This coach/manager invite must be accepted with the email it was sent to. Please sign in with the invited email, or ask your coach for a fresh link to your current email.'
        : reason === 'error'         ? 'Something went wrong accepting the invite. Try again, or contact your coach.'
        : 'We couldn\'t add you to the team automatically. Contact your coach.';
        const e = new Error(userFacing);
        e.isInviteFailure = true;
        throw e;
      }
      localStorage.removeItem('pendingInviteToken');
    }
    setUser(data.user);
    return data;
  };

  // OAuth sign-in (Google or Apple) — backend handles find-or-create
  const oauthLogin = async (provider, tokenPayload) => {
    const inviteToken = localStorage.getItem('pendingInviteToken') || undefined;
    const { data } = await api.post(`/auth/oauth/${provider}`, {
      ...tokenPayload,
      inviteToken,
    });
    localStorage.setItem('token', data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);
    if (inviteToken) localStorage.removeItem('pendingInviteToken');
    invalidateTeamRoleCache(); // see comment in login() above
    // v2.5.146 — fresh registration: no prior state to honor.
    resetOnboardingCheck();
    setUser(data.user);
    return data;
  };

  const register = async (name, email, password) => {
    const { data } = await api.post('/auth/register', { name, email, password });
    localStorage.setItem('token', data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);
    invalidateTeamRoleCache(); // see comment in login() above
    // Await invite accept (same reasoning as login — see comment above).
    const inviteToken = localStorage.getItem('pendingInviteToken');
    if (inviteToken) {
      try {
        await api.get(`/invite/${inviteToken}`);
      } catch (err) {
        localStorage.removeItem('pendingInviteToken');
        setUser(data.user);
        const reason = err.response?.data?.error;
        // Same mapping as login — see that block for context. Note
        // wrong_account only fires for coach/manager invites now
        // (Apr 2026) — player/spare invites accept any email.
        const userFacing =
          reason === 'expired'       ? 'Your invite has expired — ask your coach for a new link.'
        : reason === 'wrong_account' ? 'This coach/manager invite must be accepted with the email it was sent to. Ask your coach for a fresh link to the email you want to use.'
        : reason === 'error'         ? 'Something went wrong accepting the invite. Try again, or contact your coach.'
        : 'We couldn\'t add you to the team automatically. Contact your coach.';
        const e = new Error(userFacing);
        e.isInviteFailure = true;
        throw e;
      }
      localStorage.removeItem('pendingInviteToken');
    }
    setUser(data.user);
    return data;
  };

  const logout = async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    // Best-effort server-side revocation — don't block on failure
    if (refreshToken) {
      api.post('/auth/logout', { refreshToken }).catch(() => {});
    }
    // Apr 2026 — cancel any pending debounced last-team save BEFORE
    // tearing down auth. Without this, a user who logs out within
    // 250ms of switching teams could have their pending PATCH fire
    // AFTER a different user logs in on the same device, writing the
    // wrong user's team selection to that user's record.
    cancelPendingSave();
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    // May 6 2026 (v2.5.71) — also clear the rapid-refresh grace
    // slot (see api.js refreshAccessToken). Otherwise the previous
    // user's stale refresh token could survive logout and confuse
    // recovery logic if a new user logs in within 10s.
    localStorage.removeItem('refreshToken_prev');
    localStorage.removeItem('refreshToken_prev_expiry');
    localStorage.removeItem('pendingInviteToken');
    // Apr 2026 — last-active-team is server-side now (see migration
    // 047 + utils/lastTeam.js); the next user logging in on this device
    // gets THEIR own last_active_team_id from the /auth/login response.
    // 2026-09-16 — EXCEPT the local pin 'gd:lastTeamId' (added 2026-07-10
    // for the full-page-reload team switch), which pickActiveTeam reads
    // BEFORE the server value. Left behind after logout it would make a
    // second user who shares that team (Gilles + his spare test account,
    // say) land on the previous user's team instead of their own last
    // one. Clear it here; the server value is the cross-device memory.
    try { localStorage.removeItem('gd:lastTeamId'); } catch { /* storage unavailable */ }
    invalidateTeamRoleCache(); // clear module-level cache so next user starts fresh
    // v2.5.146 — clear onboarding-check cache so the next user
    // logging in on this device gets a fresh check, not the
    // previous user's cached result.
    resetOnboardingCheck();
    setUser(null);
  };

  // Stable reference — wrapped in useCallback so consumers can put
  // `updateUser` in effect dep arrays without causing re-runs on every
  // AuthProvider re-render. Identity only changes if setUser itself
  // ever changes (it doesn't; useState setters are stable).
  const updateUser = useCallback(
    (updates) => setUser(prev => ({ ...prev, ...updates })),
    []
  );

  return (
    <AuthContext.Provider value={{ user, loading, login, oauthLogin, register, logout, updateUser, loadUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
};
